// src/services/documentConversionService.js
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

class DocumentConversionService {
  
  async convertDocumentToImages(file) {
    const fileType = file.name.toLowerCase().split('.').pop();
    
    switch (fileType) {
      case 'pdf':
        return await this.convertPdfToImages(file);
      case 'ppt':
      case 'pptx':
        return await this.convertPptToImages(file);
      case 'doc':
      case 'docx':
        return await this.convertDocToImages(file);
      default:
        throw new Error(`Unsupported file type: ${fileType}`);
    }
  }

  async convertPdfToImages(file) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument(arrayBuffer).promise;
      const images = [];
      
      // Convert each page to image
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 2.0 }); // High resolution
        
        // Create canvas
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        
        // Render page to canvas
        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };
        
        await page.render(renderContext).promise;
        
        // Convert canvas to data URL
        const imageDataUrl = canvas.toDataURL('image/png');
        
        images.push({
          pageNumber: pageNum,
          imageData: imageDataUrl,
          width: viewport.width,
          height: viewport.height
        });
      }
      
      return {
        success: true,
        images: images,
        totalPages: pdf.numPages,
        fileType: 'pdf'
      };
      
    } catch (error) {
      console.error('PDF conversion error:', error);
      throw new Error(`Failed to convert PDF: ${error.message}`);
    }
  }

  async convertPptToImages(file) {
    // For PowerPoint files, we'll create mock slide representations
    // since browser-based PowerPoint parsing requires a backend service
    console.log('Converting PowerPoint to images using mock slide generation');
    
    try {
      // Try to use the file parser for any basic text extraction
      const FileParser = (await import('./fileParser')).default;
      const fileParser = new FileParser();
      let parsedContent;
      
      try {
        parsedContent = await fileParser.parseFile(file);
      } catch (parseError) {
        console.log('File parsing failed, using basic file info for mock slides');
        parsedContent = {
          textContent: `PowerPoint presentation: ${file.name}`,
          slideCount: null
        };
      }
      
      // Always use mock slide generation for browser compatibility
      return this.createMockPresentationImages(file, parsedContent);
      
    } catch (error) {
      console.error('PowerPoint conversion error:', error);
      // Final fallback
      return this.createMockPresentationImages(file, { 
        textContent: `PowerPoint presentation: ${file.name} - Ready for QA comparison` 
      });
    }
  }

  async convertDocToImages(file) {
    console.log('Converting Word document to images using mock generation');
    
    try {
      // Try to use the file parser for any basic text extraction
      const FileParser = (await import('./fileParser')).default;
      const fileParser = new FileParser();
      let parsedContent;
      
      try {
        parsedContent = await fileParser.parseFile(file);
      } catch (parseError) {
        console.log('File parsing failed, using basic file info for mock document');
        parsedContent = {
          textContent: `Word document: ${file.name} - Ready for QA comparison`
        };
      }
      
      // Create a visual representation of the document
      const imageData = await this.createDocumentImage(parsedContent);
      
      return {
        success: true,
        images: [{
          pageNumber: 1,
          imageData: imageData,
          width: 1920,
          height: 1080,
          content: parsedContent
        }],
        totalPages: 1,
        fileType: 'doc',
        parsedContent: parsedContent
      };
      
    } catch (error) {
      console.error('Document conversion error:', error);
      // Final fallback
      const mockContent = { 
        textContent: `Word document: ${file.name} - Ready for QA comparison` 
      };
      const imageData = await this.createDocumentImage(mockContent);
      
      return {
        success: true,
        images: [{
          pageNumber: 1,
          imageData: imageData,
          width: 1920,
          height: 1080,
          content: mockContent
        }],
        totalPages: 1,
        fileType: 'doc',
        parsedContent: mockContent,
        note: 'Mock document generated - backend parsing unavailable'
      };
    }
  }

  async createSlideImage(slide, slideNumber) {
    // Create a visual representation of a slide
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Set canvas size (standard presentation ratio)
    canvas.width = 1920;
    canvas.height = 1080;
    
    // Fill background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Add slide number
    ctx.fillStyle = '#666666';
    ctx.font = '24px Arial';
    ctx.fillText(`Slide ${slideNumber}`, 50, 50);
    
    // Add text content
    if (slide.text_content) {
      ctx.fillStyle = '#000000';
      ctx.font = '32px Arial';
      
      const words = slide.text_content.split(' ');
      const maxWidth = canvas.width - 100;
      const lineHeight = 40;
      let line = '';
      let y = 120;
      
      for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + ' ';
        const metrics = ctx.measureText(testLine);
        const testWidth = metrics.width;
        
        if (testWidth > maxWidth && n > 0) {
          ctx.fillText(line, 50, y);
          line = words[n] + ' ';
          y += lineHeight;
          
          // Prevent text overflow
          if (y > canvas.height - 100) break;
        } else {
          line = testLine;
        }
      }
      ctx.fillText(line, 50, y);
    }
    
    // Add layout type indicator
    if (slide.layout_type) {
      ctx.fillStyle = '#007acc';
      ctx.font = '20px Arial';
      ctx.fillText(`Layout: ${slide.layout_type}`, 50, canvas.height - 50);
    }
    
    // Add element counts
    if (slide.images || slide.charts || slide.tables) {
      ctx.fillStyle = '#007acc';
      ctx.font = '18px Arial';
      let elementInfo = '';
      if (slide.images) elementInfo += `Images: ${slide.images} `;
      if (slide.charts) elementInfo += `Charts: ${slide.charts} `;
      if (slide.tables) elementInfo += `Tables: ${slide.tables}`;
      
      ctx.fillText(elementInfo, canvas.width - 400, canvas.height - 50);
    }
    
    return canvas.toDataURL('image/png');
  }

  async createDocumentImage(content) {
    // Create a visual representation of a document
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    canvas.width = 1920;
    canvas.height = 1080;
    
    // Fill background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Add document title
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 36px Arial';
    ctx.fillText('Document', 50, 60);
    
    // Add content
    if (content.textContent || content.text) {
      const text = content.textContent || content.text || '';
      ctx.fillStyle = '#000000';
      ctx.font = '24px Arial';
      
      const words = text.split(' ');
      const maxWidth = canvas.width - 100;
      const lineHeight = 30;
      let line = '';
      let y = 120;
      
      for (let n = 0; n < Math.min(words.length, 200); n++) { // Limit words to prevent overflow
        const testLine = line + words[n] + ' ';
        const metrics = ctx.measureText(testLine);
        const testWidth = metrics.width;
        
        if (testWidth > maxWidth && n > 0) {
          ctx.fillText(line, 50, y);
          line = words[n] + ' ';
          y += lineHeight;
          
          if (y > canvas.height - 100) {
            ctx.fillText('...', 50, y);
            break;
          }
        } else {
          line = testLine;
        }
      }
      if (y <= canvas.height - 100) {
        ctx.fillText(line, 50, y);
      }
    }
    
    return canvas.toDataURL('image/png');
  }

  // Create mock presentation images when slide parsing fails
  async createMockPresentationImages(file, parsedContent) {
    // Estimate slide count based on file size or create default
    const estimatedSlides = Math.max(1, Math.min(20, Math.floor(file.size / 50000))); // Rough estimate
    const images = [];
    
    console.log(`Creating ${estimatedSlides} mock slides for ${file.name}`);
    
    for (let i = 1; i <= estimatedSlides; i++) {
      const mockSlide = {
        slide_number: i,
        text_content: parsedContent.textContent ? 
          parsedContent.textContent.substring((i-1) * 200, i * 200) + '...' : 
          `Slide ${i} content from ${file.name}`,
        layout_type: i === 1 ? 'title_slide' : 'content_slide',
        images: 0,
        charts: 0,
        tables: 0,
        colors: ['#000000', '#FFFFFF'],
        fonts: ['Arial', 'Calibri']
      };
      
      const imageData = await this.createSlideImage(mockSlide, i);
      
      images.push({
        pageNumber: i,
        imageData: imageData,
        width: 1920,
        height: 1080,
        slideContent: mockSlide
      });
    }
    
    return {
      success: true,
      images: images,
      totalPages: estimatedSlides,
      fileType: 'ppt',
      parsedContent: {
        ...parsedContent,
        slides: images.map(img => img.slideContent),
        slideCount: estimatedSlides
      },
      note: 'Mock slides generated - actual slide parsing unavailable'
    };
  }

  // Utility method to compare two sets of images
  prepareImagesForComparison(originalImages, designedImages) {
    const comparisons = [];
    const maxPages = Math.max(originalImages.length, designedImages.length);
    
    for (let i = 0; i < maxPages; i++) {
      const original = originalImages[i] || null;
      const designed = designedImages[i] || null;
      
      comparisons.push({
        pageNumber: i + 1,
        original: original,
        designed: designed,
        comparisonType: original && designed ? 'both' : (original ? 'missing_designed' : 'extra_designed')
      });
    }
    
    return comparisons;
  }

  // Method to optimize images for AI processing
  async optimizeImageForAI(imageDataUrl, maxWidth = 1024, maxHeight = 1024) {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate optimal dimensions while maintaining aspect ratio
        const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
        const width = img.width * ratio;
        const height = img.height * ratio;
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        const optimizedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
        
        resolve(optimizedDataUrl);
      };
      
      img.src = imageDataUrl;
    });
  }
}

export default DocumentConversionService;